/**
 * useCurrentSession — FW-200. apps/console/src/composables/useCurrentSession.ts
 * ile AYNI desen (modül-kapsamlı, tekil `reactive()` — Pinia YOK, server
 * state composable'da tutulur): router guard'ın (router/onboardingGuard.ts)
 * çözdüğü oturum, OnboardingTopBar/HomeView'in AYNI ağdan İKİNCİ KEZ
 * sormadan okuyabilmesi için burada paylaşılır.
 *
 * `resolve()` dışarıdan bir `ApiClient` alır (bkz. sessionOutcome.ts
 * başlığı): bileşen ağacının içinde (`useApiClient()`'in sonucu verilerek)
 * VE dışında (main.ts'teki router guard, createSurfaceApp()'in döndürdüğü
 * GERÇEK istemciyle) AYNI kod çalışır.
 *
 * Tek-uçuş (single-flight) + 'authenticated' önbelleği — ama apps/console'un
 * aksine 'unauthenticated'/'error' de KISA SÜRELİ tekilleştirilir çünkü bu
 * composable HEM router guard'da (her navigasyonda) HEM TopBar/HomeView'de
 * eş-zamanlı çağrılabilir; hepsi aynı in-flight promise'ı paylaşır.
 */
import { reactive, readonly, type DeepReadonly } from 'vue'
import type { ApiClient } from '@fusapp-work/api'
import { resolveSessionOutcome, type SessionResponse } from '../domain/sessionOutcome'

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error'

interface CurrentSessionStateData {
  status: SessionStatus
  session: SessionResponse | null
  errorMessage: string | null
}

function createInitialState(): CurrentSessionStateData {
  return { status: 'idle', session: null, errorMessage: null }
}

const state = reactive<CurrentSessionStateData>(createInitialState())
let inflight: Promise<CurrentSessionStateData> | null = null

export interface UseCurrentSession {
  readonly state: DeepReadonly<CurrentSessionStateData>
  /** Zaten 'authenticated' ise ağı TEKRAR sormadan mevcut durumu döner. */
  resolve(apiClient: ApiClient): Promise<DeepReadonly<CurrentSessionStateData>>
  reset(): void
}

export function useCurrentSession(): UseCurrentSession {
  async function resolve(apiClient: ApiClient): Promise<CurrentSessionStateData> {
    if (state.status === 'authenticated') return state
    if (inflight) return inflight

    state.status = 'loading'
    state.errorMessage = null

    inflight = resolveSessionOutcome(apiClient)
      .then((outcome) => {
        if (outcome.kind === 'authenticated') {
          state.status = 'authenticated'
          state.session = outcome.session
          state.errorMessage = null
        } else if (outcome.kind === 'unauthenticated') {
          state.status = 'unauthenticated'
          state.session = null
          state.errorMessage = null
        } else {
          state.status = 'error'
          state.session = null
          state.errorMessage = outcome.message
        }
        return state
      })
      .finally(() => {
        inflight = null
      })

    return inflight
  }

  function reset(): void {
    Object.assign(state, createInitialState())
    inflight = null
  }

  return { state: readonly(state) as DeepReadonly<CurrentSessionStateData>, resolve, reset }
}
